import { useTranslation } from "react-i18next";

import { AnalystPlot, hasAnalystPlotView } from "./AnalystPlot";
import { AnalystExhibitAppearance, analystExhibitFrameClassName } from "./analystExhibitFrame";
import { formatAnalystValue } from "./formatAnalystValue";
import { analystChartPlot } from "./legacy/analystChartAdapters";
import { analystLegacyPlotLabels } from "./legacy/analystLegacyPlotLabels";

import type { AnalystChartSpec } from "@digital-twins/shared-types";

interface AnalystChartProps {
  chart: AnalystChartSpec;
  index: number;
  appearance?: AnalystExhibitAppearance;
}

function isValidChart(chart: AnalystChartSpec): boolean {
  if (
    chart.schemaVersion !== "analyst-chart.v1" ||
    !Array.isArray(chart.series) ||
    chart.series.length === 0
  ) {
    return false;
  }

  if (!Array.isArray(chart.series[0].data)) {
    return false;
  }

  const categories = chart.series[0].data.map((datum) => datum.category);
  const hasValidSeries = chart.series.every(
    (series) =>
      Array.isArray(series.data) &&
      series.data.length === categories.length &&
      series.data.every(
        (datum, index) =>
          typeof datum.category === "string" &&
          datum.category === categories[index] &&
          (datum.value === null ||
            (typeof datum.value === "number" && Number.isFinite(datum.value))) &&
          typeof datum.baseN === "number" &&
          Number.isFinite(datum.baseN) &&
          typeof datum.isSuppressed === "boolean",
      ),
  );

  if (!hasValidSeries) {
    return false;
  }

  return chart.chartType !== "donut" || chart.series.length === 1;
}

function formatFallbackData(
  chart: AnalystChartSpec,
  suppressedLabel: string,
): string {
  return chart.series
    .map((series) => {
      const values = series.data
        .map((datum) =>
          datum.isSuppressed || datum.value === null
            ? `${datum.category}: ${suppressedLabel}`
            : `${datum.category}: ${formatAnalystValue(datum.value, series.valueFormat)}`,
        )
        .join(" · ");
      return chart.series.length > 1 ? `${series.label} — ${values}` : values;
    })
    .join(" | ");
}

export function AnalystChart(props: AnalystChartProps) {
  const { chart, index, appearance } = props;
  const { t } = useTranslation("app");
  const suppressedLabel = t("studyReport.analyst.suppressed");

  if (!isValidChart(chart)) {
    return (
      <div
        className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground"
        data-testid={`analyst-chart-invalid-${index}`}
      >
        {t("studyReport.analyst.chartInvalid")}
      </div>
    );
  }

  // Python's plot when the chart carries one, else the stored chart read by
  // its shape (plots plan T). Every chart draws through a plot kind's view.
  const { plot, isLegacy } = analystChartPlot(chart, analystLegacyPlotLabels(t));
  const suppressedLabels = Array.from(
    new Set(
      chart.series.flatMap((item) =>
        item.data
          .filter((datum) => datum.isSuppressed || datum.value === null)
          .map((datum) => datum.category),
      ),
    ),
  );

  return (
    <figure
      className={analystExhibitFrameClassName(appearance)}
      data-testid={`analyst-chart-${index}`}
    >
      <figcaption className="flex flex-col gap-1">
        <span className="text-base font-semibold text-foreground">{chart.title}</span>
        {chart.subtitle && (
          <span className="text-sm text-muted-foreground">{chart.subtitle}</span>
        )}
      </figcaption>
      <div className="mt-4 min-h-0">
        {hasAnalystPlotView(plot.kind) ? (
          <AnalystPlot
            plot={plot}
            index={index}
            isLegacy={isLegacy}
            opensOnDonut={chart.chartType === "donut" && !isLegacy}
          />
        ) : (
          <div
            className="flex h-full flex-col items-center justify-center gap-2 rounded-lg bg-muted/40 px-4 py-6 text-center text-sm text-muted-foreground"
            data-testid={`analyst-chart-fallback-${index}`}
          >
            <span>
              {t("studyReport.analyst.chartUnsupported", {
                chartType: chart.chartType.replace("_", " "),
              })}
            </span>
            <span>{formatFallbackData(chart, suppressedLabel)}</span>
          </div>
        )}
      </div>
      {suppressedLabels.length > 0 && (
        <div
          className="mt-1 text-xs text-muted-foreground"
          data-testid={`analyst-chart-suppressed-${index}`}
        >
          {suppressedLabels
            .map((label) =>
              t("studyReport.analyst.suppressedCategory", { label }),
            )
            .join(" · ")}
        </div>
      )}
    </figure>
  );
}
